import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import { hasRealProfileData } from '../utils/profileMeta';
import { gradeNumber } from '../utils/gradeYear';
import { track, EVENTS } from '../services/analytics';
import { isOnboardingPath } from '../utils/onboardingLanding';
import {
    fetchStructuredProfile, getCollegeList, checkCredits, updateProfileField, PROGRESS_EVENT,
} from '../services/api';

// Getting Started checklist (#561) — THREE high-value steps to the aha, not a
// tour of the app. It was 13 items, of which 7 checked off merely for visiting a
// page; worse, finishing it required 4 credits from a 3-credit free tier, so the
// progress bar could not reach 100% without paying. Now: build your profile →
// see your odds (free) → add a college and read the fit. One credit to finish,
// two left over, and the first paid action comes AFTER the student has felt the
// value for free.
//
// Steps auto-check from real outcomes (profile has data, catalog visited, a fit
// exists) — never manual ticking. Every item stays a live LINK in both states: a
// checked step is a place the student revisits, never a struck-out dead end. The
// card lives in AppLayout and observes navigation itself, so no page needs
// tracking code. Dismissal and visit history persist to the profile doc
// (cross-device) with a localStorage belt.

const DISMISS_FIELD = 'onboarding_checklist_dismissed';
const dismissKey = (email) => `gs_checklist_dismissed_${email}`;
const visitedKey = (email) => `gs_visited_${email}`;
const legacyCounselorKey = (email) => `gs_met_counselor_${email}`;
const seenKey = (email) => `gs_seen_${email}`;
const openPrefKey = (email) => `gs_open_${email}`;

// Industry-standard checklist posture (Linear/Notion): a slim collapsed
// progress row by default; auto-expanded ONLY on the user's first session.
// An explicit toggle always wins and persists.
function initialOpen(email) {
    const pref = localStorage.getItem(openPrefKey(email));
    if (pref !== null) return pref === '1';
    const firstSession = !localStorage.getItem(seenKey(email));
    localStorage.setItem(seenKey(email), '1');
    return firstSession;
}

// Paths whose visit we RECORD. These used to be checklist items in their own
// right — a 13-step bar where 7 steps checked off merely for navigating
// somewhere. That measured a tour of our surfaces, not value to the student, and
// (worse) the bar demanded 4 credits from a 3-credit free tier, so it could not
// be completed without paying. The checklist is now the three steps that reach
// the aha; these paths stay tracked so historical visit data and '/universities'
// (the free-odds signal) keep working. Every one of these pages remains in the
// nav — they simply stop masquerading as onboarding.
export const VISIT_SECTIONS = [
    { key: 'odds', label: 'See your odds', to: '/universities' },
    { key: 'roadmap', label: 'See your Roadmap', to: '/roadmap' },
    { key: 'summer', label: 'Scout programs', to: '/programs' },
    { key: 'commandcenter', label: 'Open the Playbook', to: '/command-center' },
    { key: 'research', label: 'Open your Research Notebook', to: '/research' },
    { key: 'resources', label: 'Browse the Resources library', to: '/resources' },
    { key: 'ledger', label: 'Track your admissions decisions', to: '/decision-ledger' },
];

const VISIT_PATHS = new Set(VISIT_SECTIONS.map((s) => s.to));

function loadVisited(email, profile) {
    const set = new Set();
    try {
        for (const p of JSON.parse(localStorage.getItem(visitedKey(email)) || '[]')) set.add(p);
    } catch { /* corrupt local state — the profile copy below still applies */ }
    for (const [p, v] of Object.entries(profile?.visited_sections || {})) {
        if (v) set.add(p);
    }
    // Continuity: the pre-expansion checklist tracked the counselor click.
    if (localStorage.getItem(legacyCounselorKey(email))) set.add('/command-center');
    return set;
}

// Exported for tests: the three steps that reach the aha, derived from live data.
//
// Deliberately THREE, and deliberately in this order:
//   1. profile — free, and the prerequisite for everything else.
//   2. odds    — FREE. The differentiator ("your odds at every school is
//                always free") felt before anything costs a credit, so the first
//                paid action is one the student chooses after seeing value.
//   3. fit     — 1 credit. Adding a college computes the fit, so this is one
//                step, not two: the deep read IS the aha.
//
// Total cost to finish: ONE credit of the three a free account starts with.
// The old bar demanded four and ended on a paywall.
//
// Major Map is NOT here on purpose. Fit and Major Chances read `intended_majors`
// straight off the profile; the map is an ADDITIONAL source of majors, never a
// prerequisite (only Activity Ideas is gated on it). It answers a different
// question — "what should I study?" vs "where can I get in?" — so it's handed
// off in the completion state, to the students who are actually undecided,
// instead of taxing every student a credit up front.
export function deriveChecklist({ profile, colleges, visited }) {
    const hasFit = (colleges || []).some((c) => c.fit_category || c.fit_analysis?.fit_category);
    const v = visited || new Set();
    const profileDone = hasRealProfileData(profile);
    return [
        // UNDONE → /welcome, the guided first touch (grade → doors → starting
        // picture) that also fires onboarding_started; /profile opens on a
        // blank textarea — the heaviest possible first action, especially on
        // a phone. This card was quietly routing every new student around the
        // journey built for them, and activation undercounted to match
        // (#1259 — CPO's top fix against the 60% floor). DONE → /profile,
        // where an existing profile is edited.
        { key: 'profile', label: 'Tell us about you',
          to: profileDone ? '/profile' : '/welcome', done: profileDone },
        {
            key: 'odds', label: 'See your odds', to: '/universities',
            // A visit ALONE is not this step. Odds are computed from the
            // profile, so a student who browsed the catalog first saw no odds
            // at all — checking the step off then claims they saw something
            // that could not have been on screen. (This is the same
            // navigation-as-progress signal that made the old 13-item bar
            // meaningless; it does not get a pass here.)
            done: profileDone && v.has('/universities'),
        },
        { key: 'fit', label: 'Add a college to your list', to: '/universities', done: hasFit },
    ];
}

export default function GettingStartedCard() {
    const { currentUser } = useAuth();
    const email = currentUser?.email;
    const location = useLocation();

    const [data, setData] = useState(null);      // null = loading
    const [dismissed, setDismissed] = useState(true); // assume hidden until known
    const [visited, setVisited] = useState(() => new Set());
    const [open, setOpen] = useState(() => (email ? initialOpen(email) : false));

    const toggleOpen = () => {
        setOpen((prev) => {
            const next = !prev;
            if (email) localStorage.setItem(openPrefKey(email), next ? '1' : '0');
            return next;
        });
    };

    const loadData = useCallback(() => {
        if (!email) return () => {};
        let alive = true;
        // Only what the three steps need. The old bar also fetched the major
        // map, activity ideas and agent-connection status purely to tick
        // checkboxes — three network calls per page load for items that are no
        // longer on the checklist.
        Promise.allSettled([
            fetchStructuredProfile(email),
            getCollegeList(email),
            checkCredits(email),
        ]).then(([p, c, cr]) => {
            if (!alive) return;
            const profile = p.status === 'fulfilled' && p.value?.success ? p.value.profile : null;
            setVisited(loadVisited(email, profile));
            setData({
                profile,
                colleges: c.status === 'fulfilled' && c.value?.success ? (c.value.college_list || []) : [],
                // Free-credit framing (#758): while the student still has
                // credits, the scary-sounding steps are explicitly "included".
                credits: cr.status === 'fulfilled' ? (cr.value?.credits_remaining ?? null) : null,
            });
            // Visibility is a real user setting now (Settings → Onboarding &
            // tips), so a recorded dismissal is honored unconditionally — the
            // toggle is the way back, completed or not.
            setDismissed(Boolean(profile?.[DISMISS_FIELD] || localStorage.getItem(dismissKey(email))));
        });
        return () => { alive = false; };
    }, [email]);

    useEffect(() => loadData(), [loadData]);

    // Re-fetch when a milestone mutation fires the global progress signal, so a
    // freshly generated Major Map / added college / first fit checks off
    // immediately — the card lives in AppLayout and never re-mounts, so without
    // this it would show its mount-time snapshot until a full page reload.
    useEffect(() => {
        if (!email) return undefined;
        const onProgress = () => loadData();
        window.addEventListener(PROGRESS_EVENT, onProgress);
        return () => window.removeEventListener(PROGRESS_EVENT, onProgress);
    }, [email, loadData]);

    // The card IS the visit tracker: it re-renders on every route change, so
    // landing on a tracked section marks it — no per-page wiring anywhere.
    useEffect(() => {
        if (!email || !VISIT_PATHS.has(location.pathname)) return;
        setVisited((prev) => {
            if (prev.has(location.pathname)) return prev;
            const next = new Set(prev);
            next.add(location.pathname);
            const arr = [...next];
            try { localStorage.setItem(visitedKey(email), JSON.stringify(arr)); } catch { /* private mode */ }
            // Cross-device persistence, best-effort.
            updateProfileField(email, 'visited_sections',
                Object.fromEntries(arr.map((p) => [p, true]))).catch(() => {});
            return next;
        });
    }, [email, location.pathname]);

    const items = useMemo(
        () => (data ? deriveChecklist({ ...data, visited }) : []),
        [data, visited],
    );
    const doneCount = items.filter((i) => i.done).length;
    // The first unfinished step — named in the collapsed row so the bar
    // says what to DO, not just how far along you are.
    const nextItem = items.find((i) => !i.done);

    // Fire checklist_item_completed exactly once per item, on the render where
    // it first shows done — the diff lives in localStorage so refreshes and
    // new sessions don't re-fire history.
    //
    // The same diff drives the auto-expand: the moment a step flips to done is
    // the natural "what's next?" beat, and the card lives in AppLayout, so the
    // flip lands wherever the student happens to be. Deliberately NOT on the
    // first pass of a mount — that snapshot is just where the student already
    // was, not something they just finished.
    // A step just flipped to done — held briefly so the student gets an
    // acknowledgement instead of a checkbox silently changing colour.
    const [celebrated, setCelebrated] = useState(null);
    const diffedOnce = useRef(false);
    useEffect(() => {
        if (!items.length) return;
        const firstPass = !diffedOnce.current;
        diffedOnce.current = true;
        try {
            const seen = new Set(JSON.parse(localStorage.getItem('gs_completed_tracked') || '[]'));
            const fresh = items.filter((i) => i.done && !seen.has(i.key));
            if (!fresh.length) return;
            fresh.forEach((i) => { track(EVENTS.CHECKLIST_ITEM_COMPLETED, { item: i.key }); seen.add(i.key); });
            localStorage.setItem('gs_completed_tracked', JSON.stringify([...seen]));
            // Once per completion (the localStorage diff guarantees that), and
            // transient by design: we do NOT write the open preference here, so
            // an explicit collapse still wins on every subsequent load.
            if (!firstPass) {
                setOpen(true);
                setCelebrated(fresh[0].key);
            }
        } catch { /* private mode */ }
    }, [items]);
    // PERSISTENT by design (founder-tested): a 7-second auto-clear meant the
    // congratulations — and the wizard rail to the next step — vanished before
    // the student looked down. It now stays until the CTA is taken, dismissed,
    // or the next completion replaces it. It's the hand-rail between steps.

    const allDone = items.length > 0 && doneCount === items.length;

    const dismiss = () => {
        setDismissed(true);
        if (email) {
            localStorage.setItem(dismissKey(email), '1');
            // Cross-device persistence, best-effort — localStorage already covers this device.
            updateProfileField(email, DISMISS_FIELD, true).catch(() => {});
        }
    };

    const celebratedItem = celebrated ? items.find((i) => i.key === celebrated) : null;

    // The floating bottom-left completion toast (completionNudge) was
    // removed 2026-08-18 (founder: "we can do away with it as we will be
    // changing the profile building experience anyways" — ADR 0009). The
    // inline "Nice — X done" banner below, rendered inside the card itself,
    // stays — it carries the same acknowledgement + next-step CTA without
    // a second floating surface competing for attention.

    // Hidden: while loading, on /welcome, on /settings (a utility page, not a
    // journey step — and it hosts the bar's own show/hide toggle), or when the
    // user turned the bar off (completion banner's Dismiss, or that toggle —
    // which is also the way to bring it back).
    if (!data || isOnboardingPath(location.pathname) || location.pathname === '/settings' || dismissed) return null;

    // THE HANDOFF (founder 2026-08-04: "with ['The pivotal year'] in place,
    // why do we need the onboarding above it?"): the checklist's job is to
    // carry a student to the point where we know who they are. The moment the
    // profile is real AND the grade resolves — the exact condition under
    // which YourYearPlan renders — the year plan IS the journey surface, and
    // this card retires itself. It returns for nobody twice: a student who
    // clears their grade later simply graduates from checklist to year plan.
    const p = data.profile || {};
    const yearPlanTakesOver = hasRealProfileData(p)
        && (gradeNumber(p.grade) !== null || gradeNumber(p.grade_level) !== null);
    if (yearPlanTakesOver) return null;

    if (allDone) {
        return (
            /* Completion is the right moment to hand off Major Map — it's
               offered to students who are actually undecided about direction,
               rather than charged to everyone during onboarding.
               Mid-tone forest green (founder call): calmer than the
               action-dark #1A4D2E bar — the work is done — but never fading
               into the pale page like the old #F5FAF5 did. It still carries
               two live CTAs, so it has to read as a surface, not a whisper. */
            <div className="mb-6 rounded-2xl bg-[#2D6B45] shadow-sm px-5 py-4 flex items-center justify-between gap-3" data-testid="getting-started-done">
                <p className="text-sm text-white">
                    <span className="font-semibold">You’re set up.</span>{' '}
                    <Link to="/launchpad" className="font-medium underline hover:no-underline" data-testid="getting-started-launchpad-cta">
                        Open your fit analysis
                    </Link>
                    <span className="text-white/80">{' '}— or, not sure which major to lead with?{' '}</span>
                    <Link to="/major-map" className="font-medium underline hover:no-underline" data-testid="getting-started-majormap-cta">
                        Explore your Majors
                    </Link>
                    .
                </p>
                <button onClick={dismiss} className="text-sm font-medium text-white/80 hover:text-white shrink-0">
                    Dismiss
                </button>
            </div>
        );
    }

    // Collapsed (the everyday posture): one slim progress row.
    //
    // The row used to be a single <button>, which is exactly why the next step
    // could only ever be inert text — an anchor inside a button is invalid HTML
    // and breaks keyboard/AT behaviour. Expand control and step link are now
    // SIBLINGS: most of the row still expands on click, and the step is a real
    // link to its route.
    if (!open) {
        return (
            <div
                className="w-full mb-6 rounded-xl bg-[#1A4D2E] shadow-sm px-4 py-2.5 flex items-center gap-3 hover:bg-[#215C39] transition-colors"
                data-testid="getting-started-row"
            >
                <button
                    type="button"
                    onClick={toggleOpen}
                    className="flex flex-1 min-w-0 items-center gap-3 text-left"
                    data-testid="getting-started-collapsed"
                    aria-expanded="false"
                >
                    <RocketLaunchIcon className="h-4 w-4 text-white shrink-0" />
                    <span className="text-sm font-semibold text-white whitespace-nowrap">
                        Getting started <span className="font-normal text-white/70">· {doneCount} of {items.length}</span>
                    </span>
                    <span className="flex-1 h-1.5 rounded-full bg-white/25 overflow-hidden min-w-[3rem]">
                        <span className="block h-full bg-white rounded-full transition-all duration-500" style={{ width: `${(doneCount / items.length) * 100}%` }} />
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-white/70 shrink-0" />
                </button>
                {/* The pull. Naming the step wasn't enough: it sat in a row whose
                    only action was expand/collapse, so a student who had just
                    finished one had nowhere obvious to go. Shown even when they
                    are already on that step's page — there it reads as
                    confirmation they're in the right place. */}
                {nextItem && (
                    <Link
                        to={nextItem.to}
                        onClick={() => track(EVENTS.NUDGE_CLICKED, { nudge: 'checklist_next_step', item: nextItem.key })}
                        className={`shrink-0 max-w-[55%] sm:max-w-none inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs sm:text-sm font-semibold text-[#1A4D2E] hover:bg-[#EAF4EA] transition-colors ${celebrated ? 'ring-2 ring-white/60 ring-offset-1 ring-offset-[#1A4D2E] animate-pulse' : ''}`}
                        data-testid="getting-started-next"
                    >
                        <span className="truncate">{nextItem.label}</span>
                        <ArrowRightIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div className="mb-6 rounded-2xl bg-[#1A4D2E] px-5 py-4 shadow-md" data-testid="getting-started-card">
            {/* Acknowledge the win. A checkbox quietly changing colour is not
                feedback — the student who just finished their profile should be
                told so, and pointed at what's next in the same breath. */}
            {celebratedItem && (
                <div
                    data-testid="getting-started-celebrate"
                    className="mb-3 flex items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-sm animate-fade-up"
                >
                    <CheckSolid className="h-5 w-5 shrink-0 text-[#1A4D2E]" />
                    <p className="text-sm text-[#1A2E1F] min-w-0">
                        <span className="font-semibold">Nice — {celebratedItem.label.replace(/ —.*$/, '')} done.</span>
                        {nextItem
                            ? <> Next up: <span className="font-medium">{nextItem.label.replace(/ —.*$/, '')}</span>.</>
                            : <> That&apos;s everything — your <Link to="/launchpad" className="font-medium underline">fit analysis</Link> is ready.</>}
                    </p>
                </div>
            )}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <RocketLaunchIcon className="h-4.5 w-4.5 text-white" style={{ height: '18px', width: '18px' }} />
                    </div>
                    <p className="text-sm font-semibold text-white">
                        Getting started <span className="font-normal text-white/70">· {doneCount} of {items.length}</span>
                    </p>
                </div>
                <button onClick={toggleOpen} aria-label="Collapse getting started" aria-expanded="true" className="text-white/70 hover:text-white" data-testid="getting-started-toggle">
                    <ChevronUpIcon className="h-4 w-4" />
                </button>
            </div>
            <div className="h-1.5 rounded-full bg-white/25 mb-3 overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${(doneCount / items.length) * 100}%` }} />
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-2">
                {items.map((item) => (
                    <li key={item.key}>
                        {/* Done items stay LINKS — a checked step is a place the
                            student revisits, never a dead strike-through. */}
                        <Link
                            to={item.to}
                            onClick={() => {
                                if (item.key === 'fit' && !item.done && data?.credits > 0) {
                                    track(EVENTS.NUDGE_CLICKED, { nudge: 'fit_checklist' });
                                }
                            }}
                            className={`inline-flex items-center gap-1.5 text-sm hover:underline ${item.done
                                ? 'text-white/60'
                                : 'font-medium text-white'}`}
                            data-testid={`gs-${item.done ? 'done' : 'todo'}-${item.key}`}
                        >
                            {item.done
                                ? <CheckSolid className="h-4 w-4 text-[#8FD4A8]" />
                                : <CheckCircleIcon className="h-4 w-4 text-white/45" />}
                            {item.label}
                            {!item.done && item.progress && <span className="text-xs text-white/60 font-normal">({item.progress})</span>}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
